// Angular
import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';

// RxJS
import { exhaustMap, Observable } from 'rxjs';
import { map } from 'rxjs/operators';

// Third-party
import { environment } from 'src/environments/environment';

// Data Contracts
import { DocumentHistoryResponse } from '@data-contracts';

// Local
import { ENV_CONFIG } from '@app/core/app-config/app-config.service';
import { documentsEndpoints } from '@app/types/api/endpoints/documents.endpoints';
import { EnvConfig } from '@app/types/environments';

const BASE_URL = environment.ApiServer.BaseURL;
const DOCUMENTS_ENDPOINTS = documentsEndpoints(BASE_URL);
/**
 * A service containing the Documents API endpoints.
 */
@Injectable({ providedIn: 'root' })
export class DocumentsApiService {
  /**
   * Constructor
   *
   * @param http The Angular HttpClient service.
   *
   * @param envConfig The injected environment configuration.
   */
  constructor(
    private readonly http: HttpClient,
    @Inject(ENV_CONFIG) private readonly envConfig: Observable<EnvConfig>,
  ) {}

  /**
   * A public api method to retrieve document history.
   *
   * @param documentId The documentId of the document to retrieve.
   *
   *
   * @returns An observable of the
   */
  getDocumentHistory(documentId: number): Observable<DocumentHistoryResponse> {
    const url = DOCUMENTS_ENDPOINTS.getDocumentHistory(documentId);
    return this.http.get<DocumentHistoryResponse>(url);
  }

  /**
   * A public api method to allow for the retrieval of a document.
   *
   * @param documentId The documentId of the document to retrieve.
   * @param rendering The document rendering to retrieve.
   * @param fallbackToConvertedPdf Return the converted PDF when the requested rendering doesn't exist.
   *
   * @returns An observable of the raw buffer of binary data for the document.
   */
  getDocument(
    documentId: string,
    rendering: string,
    fallbackToConvertedPdf?: boolean,
  ): Observable<ArrayBuffer> {
    const params = fallbackToConvertedPdf
      ? { fallbackToPdf: 'true' }
      : undefined;
    return this.envConfig.pipe(
      exhaustMap((envConfig: EnvConfig) => {
        return this.http.get(
          `${envConfig.ApiServer.BaseURL}/documents/documentId/${documentId}/rendering/${rendering}/download`,
          { responseType: 'arraybuffer', params },
        );
      }),
    );
  }

  /**
   * Downloads a document for a specific filing.
   *
   * @param filingUniqueId The unique identifier for the filing.
   * @param documentId The documentId of the document to download.
   *
   * @returns An observable containing both the document data and content type.
   */
  downloadDocument(
    documentId: string,
    rendering: string,
    fallbackToConvertedPdf?: boolean | null,
  ): Observable<{
    data: ArrayBuffer;
    contentType: string;
    contentDisposition: string;
  }> {
    const params =
      fallbackToConvertedPdf === null
        ? {}
        : { fallbackToPdf: fallbackToConvertedPdf.toString() };
    return this.envConfig.pipe(
      exhaustMap((envConfig: EnvConfig) => {
        return this.http
          .get(
            `${envConfig.ApiServer.BaseURL}/documents/documentid/${documentId}/rendering/${rendering}/download`,
            {
              responseType: 'arraybuffer',
              observe: 'response',
              params,
            },
          )
          .pipe(
            map(response => ({
              data: response.body as ArrayBuffer,
              contentType:
                response.headers.get('content-type') || 'application/pdf',
              contentDisposition:
                response.headers.get('content-disposition') || '',
            })),
          );
      }),
    );
  }
}
